import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { BookOpen, Loader2, LogOut, Megaphone, Send, Users } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const inputClass =
  "w-full border-0 border-b border-[rgba(212,175,55,0.3)] bg-transparent py-3 font-lora text-sm text-abbey-cream placeholder:text-abbey-cream/35 focus:border-abbey-gold focus:outline-none transition-colors duration-300";

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export default function AdminPage() {
  const [checking, setChecking] = useState(true);
  const [user, setUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginBusy, setLoginBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const [announcement, setAnnouncement] = useState({ title: "", message: "" });
  const [sendBusy, setSendBusy] = useState(false);

  const api = useCallback(async (config, retried = false) => {
    try {
      return await axios({ withCredentials: true, ...config });
    } catch (e) {
      if (e.response?.status === 401 && !retried) {
        try {
          await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
          return await api(config, true);
        } catch {
          throw e;
        }
      }
      throw e;
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const r = await api({ method: "get", url: `${API}/admin/subscribers` });
      setStats(r.data);
    } catch {
      setStats(null);
    }
  }, [api]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api({ method: "get", url: `${API}/auth/me` });
        setUser(r.data);
        await loadStats();
      } catch {
        setUser(null);
      } finally {
        setChecking(false);
      }
    })();
  }, [api, loadStats]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loginBusy) return;
    setLoginBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, loginForm, { withCredentials: true });
      setUser(data);
      toast.success(`Welcome back, ${data.name || "Father"}.`);
      await loadStats();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoginBusy(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    setUser(null);
    setStats(null);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (sendBusy) return;
    setSendBusy(true);
    try {
      const { data } = await api({ method: "post", url: `${API}/admin/announcement`, data: announcement });
      toast.success(`Announcement sent to ${data.count} subscriber${data.count === 1 ? "" : "s"}.`);
      setAnnouncement({ title: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSendBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-abbey-dark px-6 py-24">
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[80vw] w-[80vw] -translate-x-1/2 -translate-y-1/2 glow-orb" />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: EASE }}
        className="relative w-full max-w-2xl"
        data-testid="admin-page"
      >
        <div className="mb-12 text-center">
          <a href="/" data-testid="admin-back-link" className="inline-flex items-center gap-3">
            <BookOpen className="h-5 w-5 text-abbey-gold" strokeWidth={1.5} />
            <span className="font-cinzel text-sm font-semibold tracking-[0.25em] text-abbey-gold">
              FRJOHNBOOK.SHOP
            </span>
          </a>
          <h1 className="mt-6 font-cormorant text-4xl font-medium text-abbey-cream md:text-5xl">
            The Vestry
          </h1>
          <p className="mt-3 font-cinzel text-[10px] tracking-[0.35em] text-abbey-cream/50">
            AUTHOR'S STUDY — PRIVATE
          </p>
        </div>

        {checking ? (
          <div className="flex justify-center py-12" data-testid="admin-loading">
            <Loader2 className="h-6 w-6 animate-spin text-abbey-gold" strokeWidth={1.5} />
          </div>
        ) : !user ? (
          <form data-testid="admin-login-form" onSubmit={handleLogin} className="space-y-10">
            <div>
              <label htmlFor="admin-email" className="font-cinzel text-[10px] tracking-[0.3em] text-abbey-gold">
                EMAIL
              </label>
              <input
                id="admin-email"
                data-testid="admin-email-input"
                type="email"
                required
                value={loginForm.email}
                onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="mail@fatherjohnbooks.shop"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="font-cinzel text-[10px] tracking-[0.3em] text-abbey-gold">
                PASSWORD
              </label>
              <input
                id="admin-password"
                data-testid="admin-password-input"
                type="password"
                required
                value={loginForm.password}
                onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="••••••••"
                className={inputClass}
              />
            </div>
            <button
              data-testid="admin-login-btn"
              type="submit"
              disabled={loginBusy}
              className="inline-flex w-full items-center justify-center gap-3 border border-abbey-gold bg-abbey-gold px-10 py-4 font-cinzel text-[11px] font-semibold tracking-[0.3em] text-abbey-dark transition-colors duration-300 hover:bg-transparent hover:text-abbey-gold disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loginBusy && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />}
              {loginBusy ? "SIGNING IN..." : "SIGN IN"}
            </button>
          </form>
        ) : (
          <div data-testid="admin-dashboard" className="space-y-14">
            <div className="flex items-center justify-between border-b border-[rgba(212,175,55,0.2)] pb-6">
              <p className="font-cormorant text-xl italic text-abbey-cream/80">
                Peace be with you, {user.name || "Father"}.
              </p>
              <button
                data-testid="admin-logout-btn"
                onClick={handleLogout}
                className="inline-flex items-center gap-2 font-cinzel text-[10px] tracking-[0.25em] text-abbey-cream/60 transition-colors duration-300 hover:text-abbey-gold"
              >
                <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} />
                SIGN OUT
              </button>
            </div>

            <div className="flex items-center gap-5 border border-[rgba(212,175,55,0.25)] p-6" data-testid="admin-subscriber-count">
              <Users className="h-6 w-6 text-abbey-gold" strokeWidth={1.5} />
              <div>
                <p className="font-cormorant text-3xl font-semibold text-abbey-cream">
                  {stats ? stats.count : "—"}
                </p>
                <p className="font-cinzel text-[10px] tracking-[0.25em] text-abbey-cream/50">
                  NEWSLETTER SUBSCRIBERS
                </p>
              </div>
            </div>

            <form data-testid="admin-announcement-form" onSubmit={handleSend} className="space-y-10">
              <div className="flex items-center gap-3">
                <Megaphone className="h-4 w-4 text-abbey-gold" strokeWidth={1.5} />
                <p className="font-cinzel text-[11px] tracking-[0.35em] text-abbey-gold">
                  SEND A LAUNCH ANNOUNCEMENT
                </p>
              </div>
              <div>
                <label htmlFor="announcement-title" className="font-cinzel text-[10px] tracking-[0.3em] text-abbey-gold">
                  BOOK / SUBJECT
                </label>
                <input
                  id="announcement-title"
                  data-testid="admin-announcement-title-input"
                  type="text"
                  required
                  maxLength={140}
                  value={announcement.title}
                  onChange={(e) => setAnnouncement((f) => ({ ...f, title: e.target.value }))}
                  placeholder="My new book is here"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="announcement-message" className="font-cinzel text-[10px] tracking-[0.3em] text-abbey-gold">
                  MESSAGE
                </label>
                <textarea
                  id="announcement-message"
                  data-testid="admin-announcement-message-input"
                  required
                  maxLength={4000}
                  rows={5}
                  value={announcement.message}
                  onChange={(e) => setAnnouncement((f) => ({ ...f, message: e.target.value }))}
                  placeholder="Dear readers, I'm delighted to share..."
                  className={`${inputClass} resize-none`}
                />
              </div>
              <button
                data-testid="admin-announcement-send-btn"
                type="submit"
                disabled={sendBusy}
                className="inline-flex w-full items-center justify-center gap-3 border border-abbey-gold bg-abbey-gold px-10 py-4 font-cinzel text-[11px] font-semibold tracking-[0.3em] text-abbey-dark transition-colors duration-300 hover:bg-transparent hover:text-abbey-gold disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sendBusy ? (
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
                ) : (
                  <Send className="h-4 w-4" strokeWidth={1.5} />
                )}
                {sendBusy ? "SENDING..." : "SEND TO ALL SUBSCRIBERS"}
              </button>
            </form>
          </div>
        )}
      </motion.div>
    </div>
  );
}
